import React, { useState } from 'react'
import { useDispatch, batch } from 'react-redux'

import CrossHairs from '../../icons/crosshairs.svg'
import DeviationIcon from '../../icons/deviation.svg'
import PlusIcon from '../../icons/plus.svg'
import DownloadIcon from '../../icons/download.svg'

import Loading from '../../components/Loading'
import Title from '../../components/Title'
import MetaData from '../../components/MetaData'
import PlaceHolder from '../../components/PlaceHolder'
import Link from '../../components/Link'
import TrailMeta from '../../components/TrailMeta'
import ImageSlideshow from '../../components/ImageSlideshow'
import { IconList } from '../../components/Icons'
import Distance from '../../components/Distance'
import Stats from '../../components/Stats'
import TrailNavigation from '../../components/TrailNavigation'
import UserPositionManager from '../../components/UserPositionManager'
import UserPositionControl from '../../components/UserPositionControl'

import { settings } from '../../conf'
import { isTouchDevice } from '../../utils'

import { reverse, absoluteUrl } from '../../urls'

import {
  setInfoPanel,
  showExportPanel,
  showMapPanel,
  useIsMobile,
  useHaveInfoPanel,
} from '../../slices/ui'

import {
  setProfilePin
} from '../../slices/profile'

import {
  useUserPositionOnTrailIsReached
} from '../../slices/userposition'

import {
  setMapExtent,
  setMapPin,
} from '../../slices/map'

import {
  useQuadhashStats,
  useStepStats,
  useUrlParams,
  useNavigatorOnline
} from '../../hooks'

import {
  useAlternativeStops,
  useRoute,
  useRouteNearestTrail,
  useSteps,
  useTrailImages,
} from '../../api'

import {
  DEVIATION_OF_TRAIL,
  DOWNLOAD,
  PLACES_LIST,
  ROUTES_DESCRIPTION,
  SHOW_ALTERNATIVE_STOPS,
  SHOW_ON_MAP,
  WHERE_TO_SLEEP,
} from '../../trans'

const ListItem = (props) => {
  const { index, className, title, subtitle, amenities, is_deviation, distance_from_trail, 
    stats, onClick, onMouseEnter, onMouseLeave, onActionClick } = props
  const icons = amenities ? Object.keys(amenities) : undefined

  return (
        <li className={'pwa__list__item fade-in ' + className }
            style={{ animationDelay: `${0.1 + 0.03 * index}s` }}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>

          <div title={ `${window.gettext('go to place')} ${title}`} className="pwa__list__container">
            {is_deviation && (
            <i className="pwa__list__deviation">
              <DeviationIcon/>
              <Distance value={distance_from_trail} className="pwa__list__distance"/>
              { DEVIATION_OF_TRAIL }
            </i>
            )}

          <h3 className="pwa__list__title inherit">
            <PlaceHolder value={title}>
              {title}
              {subtitle && (
                <span className="pwa__list__subtitle">
                  , {subtitle}
                </span>
              )}
            </PlaceHolder>
          </h3>

          <div className="pwa__amenities__list">
            <PlaceHolder value={icons}>
              <IconList icons={icons} />
            </PlaceHolder>
          </div>

          { stats || <Stats/> }

        </div>

        <div className="pwa__list__actions">
          <PlaceHolder value={onActionClick}>
            <button className="button circular pwa__list__action"
                    title={SHOW_ON_MAP}
                    onClick={onActionClick}>
                <CrossHairs title={window.gettext('show on map')} />
            </button>
          </PlaceHolder>
        </div>
      </li>
  )
}

const StepStas = (props) => {
  const { route_id, trail_id,  step } = props
  const { length, time } = useStepStats({ step: step, route_id: route_id })
  return <Stats length={length} time={time} elevation={step.elevation} />
}

const Step = (props) => {
  const { step, index, route_id, trail_id } = props
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const className = useUserPositionOnTrailIsReached(!!step && step.length) ? 'reached' : ''

  const onMouseEnter = !!step && !isTouchDevice && !isMobile
    ? (e) => batch(() => {
        dispatch(setMapPin(step.lnglat))
        if (!step.is_deviation) dispatch(setProfilePin([step.length, step.elevation_on_trail, step.time]))
      })
    : undefined

  const onMouseLeave = !!step && !isTouchDevice && !isMobile
    ? (e) => batch(() => {
        dispatch(setMapPin(null))
        dispatch(setProfilePin(null))
      })
    : undefined

  const onClick = step
    ? (e) => batch(() => {
        dispatch(showMapPanel(true))
        dispatch(setMapExtent({ bounds: step.extent }))
        dispatch(setInfoPanel({
          type: 'step',
          step_trail_id: trail_id,
          step_id: step.step_id
        }))
      })
    : undefined

  const onActionClick = step
    ? (e) => batch(() => {
        e.stopPropagation()
        dispatch(showMapPanel(true))
        dispatch(setMapExtent({ bounds: step.extent }))
        dispatch(setMapPin(step.lnglat))
        if (!step.is_deviation) dispatch(setProfilePin([step.length, step.elevation_on_trail, step.time]))
      })
    : undefined

  const stats = step && <StepStas step={step} trail_id={trail_id} route_id={route_id}/>

  const { title, subtitle, is_deviation, amenities, distance_from_trail } = step || {}

  return <ListItem index={index}
                   className={className}
                   title={title}
                   subtitle={subtitle}
                   amenities={amenities}
                   is_deviation={is_deviation}
                   distance_from_trail={distance_from_trail}
                   stats={stats}
                   onClick={onClick}
                   onMouseEnter={onMouseEnter}
                   onMouseLeave={onMouseLeave}
                   onActionClick={onActionClick} />
}

const AlternativeStopStas = (props) => {
  const { route_id, trail_id, waypoint } = props
  const { length, time } = useQuadhashStats({ quadhash: waypoint.quadhash, route_id: route_id, trail_id: trail_id })
  return <Stats length={length} time={time} elevation={waypoint.elevation} />
}

const AlternativeStop = (props) => {
  const { waypoint, index, route_id, trail_id } = props
  const { title, subtitle, amenities, quadhash } = waypoint || {}
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const { distance_from_trail } = useRouteNearestTrail({
    routeId: route_id,
    quadhash: quadhash
  }, { skip: !quadhash }) || {}
  const is_deviation = distance_from_trail > 100
  const stats = waypoint && <AlternativeStopStas waypoint={waypoint} route_id={route_id} trail_id={trail_id}/>

  const onMouseEnter = !!waypoint && !isTouchDevice && !isMobile
    ? (e) => batch(() => {
        dispatch(setMapPin(waypoint.lnglat))
      })
    : undefined

  const onMouseLeave = !!waypoint && !isTouchDevice && !isMobile
    ? (e) => batch(() => {
        dispatch(setMapPin(null))
        dispatch(setProfilePin(null))
      })
    : undefined

  const onClick = waypoint
    ? (e) => batch(() => {
        dispatch(showMapPanel(true))
        dispatch(setMapExtent({ bounds: waypoint.extent }))
        dispatch(setInfoPanel({
          type: 'waypoint',
          quadhash: waypoint.quadhash
        }))
      })
    : undefined

  const onActionClick = waypoint
    ? (e) => batch(() => {
        e.stopPropagation()
        dispatch(showMapPanel(true))
        dispatch(setMapExtent({ bounds: waypoint.extent }))
        dispatch(setMapPin(waypoint.lnglat))
      })
    : undefined

  return <ListItem index={index}
                   className="alternative"
                   title={title}
                   subtitle={subtitle}
                   amenities={amenities}
                   is_deviation={is_deviation}
                   distance_from_trail={distance_from_trail}
                   stats={stats}
                   onClick={onClick}
                   onMouseEnter={onMouseEnter}
                   onMouseLeave={onMouseLeave}
                   onActionClick={onActionClick} />
}

const StepList = (props) => {
  const { steps, route_id, trail_id } = props
  return steps.map((step, index) => <Step step={step} key={`${trail_id}-${index}`} index={index} route_id={route_id} trail_id={trail_id}/>)
}

const AlternativeStopsList = (props) => {
  const { route_id, trail_id } = props
  const [viewAlternative, setViewAlternative] = useState(false)
  const alternativeStops = useAlternativeStops({ routeId: route_id, trailId: trail_id })
  if (!alternativeStops || alternativeStops.length === 0) return null
  if (!viewAlternative) {
    return (
      <button onClick={() => setViewAlternative(true)} className="pwa__alternative__stop button basic">
        <PlusIcon aria-hidden={true}/>
        {SHOW_ALTERNATIVE_STOPS}
      </button>
    )
  } else {
    return alternativeStops.map((waypoint, index) => <AlternativeStop waypoint={waypoint} key={index} index={index} route_id={route_id} trail_id={trail_id} />)
  }
}

const TrailMetaData = (props) => {
  const {route, trail, steps} = props
  const haveInfoPanel = useHaveInfoPanel() 
  if (!route || !trail || !steps || haveInfoPanel) return null
  const description = ROUTES_DESCRIPTION({route_title:`${trail.title} - ${trail.subtitle}`})
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Trip",
    "name":  `${trail.title} - ${trail.subtitle}`,
    "description": trail.description,    
    "offers": {
      "@type": "Offer",
      "price": "0"
    },
    "provider": {
      "@type": "Organization",
      "name": "Forwalk.org",
      "url": "https://forwalk.org",
      "logo": settings.STATIC_URL + 'images/logo_publisher.svg'
    },
    "partOfTrip" : {
      "@type": "Trip",
      "name":  `${route.title} - ${route.subtitle}`,
      "url": absoluteUrl(reverse(route.route_id))
    },
    "itinerary" : []
  }

  for (var step of steps ){
    jsonLd['itinerary'].push({
        "@type": "Place",
        "name": `${step.title} - ${step.subtitle}`,
        "url": absoluteUrl(reverse(route.route_id, trail.trail_id)+`?i=s-${trail.trail_id}-${step.step_id}`)
    })
  }

  return <MetaData title={PLACES_LIST}
                   description={description} 
                   jsonLd={jsonLd} />
}



const Page = (props) => {
  const { route_id, trail_id } = props
  const { isOnline } = useNavigatorOnline()
  const dispatch = useDispatch()
  const route = useRoute({ routeId: route_id })
  const steps = useSteps({ routeId: route_id, trailId: trail_id })
  const images = useTrailImages({ routeId: route_id, trailId: trail_id })

  if (!route) return (<Loading/>)

  const trail = route.trails[trail_id - 1]
  const backLink = (route.trails.length === 1) ? undefined : reverse(route_id)
  const prevTrail = (trail_id === 1) ? null : reverse(route_id, trail_id - 1)
  const nextTrail = (route.trails.length === trail_id) ? null : reverse(route_id, trail_id + 1)
  const stepList = steps || new Array(trail.steps_count).fill(null)
  const whereToSleep = reverse(route_id, trail_id, { type: 'trail', trail_id: trail_id })

  return (
    <>
      <TrailMetaData route={route} trail={trail} steps={steps}/>
      <ImageSlideshow images={images} key={trail_id}/>
      <Title title={trail.title} subtitle={trail.subtitle} className="options">
        <button className="button pwa__header__button" 
            disabled={!isOnline}
            onClick={() => dispatch(showExportPanel(true)) }>
            <DownloadIcon/>
            {DOWNLOAD}
        </button>
      </Title>
      <TrailMeta metadata={trail}/>

      { (route.trails.length > 1) &&
        <TrailNavigation backLink={backLink}
                         prevTrail={prevTrail}
                         nextTrail={nextTrail}/>
      }

      <ol className="pwa__list steps action">
        <StepList steps={stepList} key={`stepList-${trail_id}`} route_id={route_id} trail_id={trail_id} />
        <AlternativeStopsList key={`alternativeStopList-${trail_id}`} route_id={route_id} trail_id={trail_id}/>
      </ol>

      <UserPositionControl/>
      <UserPositionManager/>

      <Link to={ whereToSleep }
            title={window.gettext('where to sleep') }
            className="button primary pwa__footer_button">
            {WHERE_TO_SLEEP}
      </Link>

    </>
  )
}


const TrailPage = () => {
  const { route_id, trail_id } = useUrlParams(['route_id', 'trail_id'])
  return <Page key={`${route_id}-${trail_id}`} route_id={route_id} trail_id={trail_id}/>
}

export default TrailPage
